// Las dos bases de API se deciden con variables de entorno, y los literales de
// abajo son solo la última red de seguridad por si una llega vacía en runtime.
// Cambiar de host no debe obligar a tocar código. Ver
// `docs/DESLIGAR-DE-RENDER.md`.
//
// Los literales son dominios PROPIOS, verificados en Render el 8/08/2026.
// Apuntan hoy a Render mediante un CNAME en IONOS, así que un cambio de
// proveedor se resuelve reapuntando ese CNAME, sin tocar código.
//
// Antes aquí había hostnames de Render, y eso costó dos averías en producción:
// la Fase 0 del plan de fusión dedujo uno del `name:` de `render.yaml` —fichero
// que describe cómo se creó el servicio, no cómo se llama ahora— y se equivocó.
// Con un dominio nuestro el problema desaparece de raíz: ya no hay ningún
// nombre de proveedor que acertar.
function resolveBase(raw: string | undefined, fallback: string): string {
  const v = raw?.trim().replace(/^['"]+|['"]+$/g, '').trim().replace(/\/+$/, '');
  return v && v.length > 0 ? v : fallback;
}

const API_BASE = resolveBase(
  import.meta.env.VITE_API_BASE_URL,
  'https://api-app.evangelizaciondigitalhuelva.org/api',
);

// Antes esta base estaba fijada en el código sin forma de sobreescribirla, así
// que el panel seguía atado a Render aunque el resto se moviera.
const WEB_API_BASE = resolveBase(
  import.meta.env.VITE_WEB_API_BASE_URL,
  'https://api.evangelizaciondigitalhuelva.org/api',
);

const TOKEN_KEY = 'admin_access_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const detail = (data && typeof data === 'object' && 'detail' in (data as Record<string, unknown>))
      ? String((data as Record<string, unknown>).detail)
      : (typeof data === 'string' ? data : `HTTP ${res.status}`);
    throw new ApiError(detail, res.status);
  }
  return data as T;
}

export const api = {
  get: <T = unknown>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T = unknown>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T = unknown>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export { API_BASE, WEB_API_BASE };
